import { createFrontendModule } from '@backstage/frontend-plugin-api';
import orgPlugin from '@backstage/plugin-org/alpha';
import {
  GroupProfileCard,
  MembersListCard,
  OwnershipCard,
} from '@backstage/plugin-org';
import { GroupProfileRows } from './GroupProfileRows';
import { MemberCards } from './MemberCards';
import { OwnershipTiles } from './OwnershipTiles';

/*
 * The org plugin's Group/User cards, re-skinned to the portal's card grammar.
 *
 * Both are overridden through `factory` rather than `params` so the vendor's
 * config schema still drives the card — `ownedKinds` in app-config is what puts
 * the custom `Repository` kind in the ownership tally, and a bare `params`
 * override would replace the loader that reads it. `originalFactory({ params })`
 * takes precedence over the params the plugin's own factory passes, so only the
 * loader changes; filter, type and config stay the plugin's.
 *
 * Mirrors the plugin's own defaults where it reads config (see
 * `@backstage/plugin-org/alpha`), so behaviour is unchanged with no config set.
 */
const DEFAULT_OWNED_KINDS = ['Component', 'API', 'System', 'Resource'];

const ownershipCard = orgPlugin
  .getExtension('entity-card:org/ownership')
  .override({
    factory(originalFactory, { config }) {
      return originalFactory({
        params: {
          loader: async () => (
            <OwnershipTiles>
              <OwnershipCard
                relationAggregation={config.initialRelationAggregation}
                hideRelationsToggle={
                  config.showAggregateMembersToggle === undefined
                    ? undefined
                    : !config.showAggregateMembersToggle
                }
                entityFilterKind={config.ownedKinds ?? DEFAULT_OWNED_KINDS}
              />
            </OwnershipTiles>
          ),
        },
      });
    },
  });

const membersListCard = orgPlugin
  .getExtension('entity-card:org/members-list')
  .override({
    factory(originalFactory, { config }) {
      return originalFactory({
        params: {
          loader: async () => (
            <MemberCards>
              <MembersListCard
                relationAggregation={config.initialRelationAggregation}
                showAggregateMembersToggle={config.showAggregateMembersToggle}
              />
            </MemberCards>
          ),
        },
      });
    },
  });

/* Drops the card's dead "N/A" rows; see `GroupProfileRows`. */
const groupProfileCard = orgPlugin
  .getExtension('entity-card:org/group-profile')
  .override({
    params: {
      loader: async () => (
        <GroupProfileRows>
          <GroupProfileCard />
        </GroupProfileRows>
      ),
    },
  });

export const orgCardsModule = createFrontendModule({
  pluginId: 'org',
  extensions: [groupProfileCard, ownershipCard, membersListCard],
});
